import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import viVN from "antd/locale/vi_VN";
import "dayjs/locale/vi";
import dayjs from "dayjs";
import i18n from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import useNotificationWebSocket from "./components/Notification/useNotificationWebSocket";
import { antdTheme } from "./themes/antdTheme";

import { useSelector } from "react-redux";
import LogoSpinner from "./components/Commons/Loading/LogoSpinner";
import CustomLoading from "./components/SplashScreen";
import DownloadProgressPanel from "./components/VMRS/ExploreArchive/DownloadProgressPanel";
import { bottomMenu, topMenu } from "./components/VMRS/ExploreArchive/MockData";
import { LocalStorageKey } from "./constants/localStorageKeys";
import { ESttausCode } from "./enums";
import { getStaticConfigs, isAuthenticated, loadStaticConfigs } from "./helpers/common";
import useBeforeUnload from "./hooks/useBeforeUnload";
import RouterPage from "./router/RouterPage";
import
{
  getCommonSystemAsync,
  getInforAsync,
  getNotiAgentTopicAsync,
  setAllProviders,
  setCommonSystem,
  setDataStatusOrder,
  setNotiAgentTopic,
  setTheme
} from "./store/reducers/appCommonSlice";
import { getImgsAsync, setArrIdImage } from "./store/reducers/cartSlice";
import
{
  clearAllDownloads,
} from "./store/reducers/downloadSlice";
import
{
  getSideBottomMenuAsync,
  getSideMenuAsync,
  setSideBottomMenuConfig,
  setSideMenuConfig,
} from "./store/reducers/layoutSlice";
import
{
  getProviderAsync
} from "./store/reducers/mapTaskingSlice";
import { getStatusOrderAsync } from "./store/reducers/orderSlice";
import { RootState } from "./store/store";
import { ThemeProvider } from "./themes/ThemeProvider";
import { downloadManager } from "./utils/downloadManager";

// OAUTH2 + no token means the user can only be sent to /login and no global API
// is called — so we can skip the loader entirely and land on login instantly.
// Read fresh from localStorage on each call so it flips to false right after the
// token is stored at login.
const isOauth2WithoutToken = (): boolean =>
{
  try
  {
    const cfg = JSON.parse(localStorage.getItem("STATIC_CONFIG") ?? "{}");
    return (
      cfg?.REACT_APP_AUTHENTICATION_METHOD === "OAUTH2" &&
      !localStorage.getItem("token")
    );
  } catch
  {
    return false;
  }
};

// The URL the user first landed on, captured once when the bundle loads — before
// any auth redirect rewrites it to /login. Used to send the user back to their
// original deep link (e.g. /order/management-tasking/382) after they sign in.
const INITIAL_URL =
  window.location.pathname + window.location.search + window.location.hash;

// Remember where to return after login (skip trivial "/" and the login page).
const saveRedirectTarget = (): void =>
{
  if (INITIAL_URL && INITIAL_URL !== "/" && !INITIAL_URL.startsWith("/login"))
  {
    localStorage.setItem(LocalStorageKey.REDIRECT_AFTER_LOGIN, INITIAL_URL);
  }
};

// Read & clear the saved return target; fall back to "/" when there is none.
const consumeRedirectTarget = (): string =>
{
  const target = localStorage.getItem(LocalStorageKey.REDIRECT_AFTER_LOGIN);
  localStorage.removeItem(LocalStorageKey.REDIRECT_AFTER_LOGIN);
  return target && !target.startsWith("/login") ? target : "/";
};

export default function App()
{
  // Follows the language switcher in the header, which calls i18n.changeLanguage.
  const { i18n: i18nInstance } = useTranslation();
  const isEnglish = (i18nInstance.language || "vi").split("-")[0] === "en";

  // The picker takes its month and weekday names from dayjs, not from AntD.
  dayjs.locale(isEnglish ? "en" : "vi");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Decide synchronously whether the global loader should show on first paint.
  // OAUTH2 + no token lands straight on /login (no global API), so start with the
  // loader OFF — no CustomLoading flash. The loader only appears after the user
  // clicks login (see the "app:login" handler).
  const [loadingGlobal, setLoadingGlobal] = useState(
    () => !isOauth2WithoutToken(),
  );
  const downloadingFiles = useSelector(
    (state: RootState) => state.download.downloadingFiles
  );

  // Warn before closing/reloading the tab while a download is being prepared —
  // leaving the page drops the polling that watches the zip job. A failed entry
  // waiting for a retry isn't polling anything, so it doesn't warrant a warning.
  useBeforeUnload(
    downloadingFiles.some(
      (f: any) => f.status !== "error" && f.status !== "completed"
    )
  );

  // A failed zip job is re-runnable: the endpoint is idempotent and resets it.
  const handleRetry = (id: string) =>
  {
    downloadManager.retry(id);
  };

  const handleCancelItem = (id: string) =>
  {
    downloadManager.cancelById(id);
  };

  // WebSocket connection for real-time notifications (connects automatically on app load)
  useNotificationWebSocket();

  const getCart = async () =>
  {
    // @ts-ignore
    const res = await dispatch(getImgsAsync({})).unwrap();

    if (res?.status === ESttausCode.OK)
    {


      dispatch(
        setArrIdImage(
          res?.data?.data?.items?.map(
            (itemM: any) =>
              itemM?.stac_item_id
          ),
        ),
      );
    }
  };

  const getStatus = async () =>
  {

    // @ts-ignore
    const resProvider = await dispatch(getProviderAsync()).unwrap();

    dispatch(
      setAllProviders(
        resProvider?.data?.data?.filter((item: any) => item?.is_active) ?? [],
      ),
    );


    // @ts-ignore
    const resStatus = await dispatch(
      // @ts-ignore
      getStatusOrderAsync(),
    ).unwrap();

    dispatch(setDataStatusOrder(resStatus?.data?.data ?? []));
  };

  const getBaseMap = async () =>
  {
    console.log('getBasemap');
    // const res = await basemapService.GetbaseMap();
    // const visibleBasemaps = res?.data?.data?.configs?.filter(
    //   (item?: any) => item?.visibility,
    // );

    // if (localStorage.getItem("basemapSelected"))
    // {
    //   dispatch(
    //     setBasemapSelected(
    //       JSON.parse(localStorage.getItem("basemapSelected") ?? ""),
    //     ),
    //   );
    // } else
    // {
    //   dispatch(setBasemapSelected(visibleBasemaps?.[0]));
    //   localStorage.setItem(
    //     "basemapSelected",
    //     JSON.stringify(visibleBasemaps?.[0]),
    //   );
    // }
  };

  // The bundled menus are a last resort: their entries are demo data, not the
  // user's, so standing in for a failed request has to be said out loud. It used
  // to happen in silence, and only a manual page reload replaced them.
  const warnBundledMenu = (which: string) =>
    console.error(
      `${which} menu unavailable — falling back to the bundled demo menu`,
    );

  const getSideMenu = async () =>
  {
    // @ts-ignore
    const res = await dispatch(getSideMenuAsync()).unwrap();

    if (!res) warnBundledMenu("Side");

    dispatch(setSideMenuConfig(res ?? topMenu));
  };

  const getSideBottomMenu = async () =>
  {
    // @ts-ignore
    const res = await dispatch(getSideBottomMenuAsync()).unwrap();

    if (!res) warnBundledMenu("Bottom");

    dispatch(setSideBottomMenuConfig(res ?? bottomMenu));
  };

  const getCommonSystem = async () =>
  {
    // @ts-ignore
    const resTheme = await dispatch(getCommonSystemAsync()).unwrap();

    // dispatch(setTheme(resTheme?.data?.theme_setting?.theme));
    dispatch(setTheme('dark'))

    dispatch(setCommonSystem(resTheme?.data));
  };

  const getNotiAgentTopic = async () =>
  {
    try
    {
      // @ts-ignore
      const res = await dispatch(getNotiAgentTopicAsync()).unwrap();
      const topic = res?.data?.data?.[0]?.value;

      if (topic)
      {
        dispatch(setNotiAgentTopic(topic));
      }
    } catch (e)
    {
      console.error(e);
    }
  };

  const loadGlobalData = async () =>
  {
    // Hard gate: never fire any global API (web-vmrs-common, whoami, ...) unless
    // the session is authenticated. For COOKIES this is always true; for OAUTH2
    // it requires a token, so the /login screen stays completely API-silent.
    if (!isAuthenticated())
    {
      return;
    }

    try
    {

      const staticConfigs = await getStaticConfigs();
      const isOauth2 =
        staticConfigs?.REACT_APP_AUTHENTICATION_METHOD === "OAUTH2";

      const tasks: Promise<any>[] = [
        getCommonSystem(),
        getSideMenu(),
        getSideBottomMenu(),
        getStatus(),
        getBaseMap(),
        // getCart(),
      ];

      // OAUTH2 doesn't use the Kratos /sessions/whoami profile — skip it.
      if (!isOauth2)
      {
        // @ts-ignore
        tasks.push(dispatch(getInforAsync()));
      } else
      {
        // Only OAUTH2 connects the notification websocket by topic, so only
        // OAUTH2 needs the topic config fetched.
        tasks.push(getNotiAgentTopic());
      }

      await Promise.all(tasks);

      if (!window.localStorage.getItem("language"))
      {
        window.localStorage.setItem("language", "vi");
      }
      if (!window.localStorage.getItem("viewMap"))
      {
        window.localStorage.setItem("viewMap", "basic");
      }

      i18n.changeLanguage(window.localStorage.getItem("language") ?? "vi");
    } catch (e)
    {
      console.error(e);
    } finally
    {
      // setLoadingGlobal(false);
    }
  };

  const checkAuth = async () =>
  {
    const jsonObject = await loadStaticConfigs({ dispatch });
    const token = localStorage.getItem("token");

    if (jsonObject?.REACT_APP_AUTHENTICATION_METHOD === "OAUTH2")
    {
      if (!token)
      {
        // Landed on a protected deep link without a token — remember it so we
        // can return here right after the user signs in.
        saveRedirectTarget();
        if (window.location.pathname !== "/login")
        {
          navigate("/login", { replace: true });
        }
        setLoadingGlobal(false);
        return;
      }

      if (window.location.pathname === "/login")
      {
        if (token && localStorage.getItem("token_type"))
        {
          navigate(consumeRedirectTarget(), { replace: true });
        }

        setLoadingGlobal(false);
        return;
      }
    } else if (jsonObject?.REACT_APP_AUTHENTICATION_METHOD === "COOKIES")
    {
      if (localStorage.getItem("token"))
      {
        localStorage.removeItem("token");
      }
      if (localStorage.getItem("token_type"))
      {
        localStorage.removeItem("token_type");
      }
    }

    await loadGlobalData();
    setLoadingGlobal(false);
  };

  useEffect(() =>
  {
    checkAuth();

    // Client-side login: instead of a full page reload (which flashes a blank
    // window before React re-mounts), the login page dispatches "app:login"
    // after storing the token. We then show CustomLoading, load all global
    // data, and navigate to "/" — so the ONLY loader the user ever sees is the
    // React CustomLoading (no mismatched static splash).
    const onLogin = async () =>
    {
      setLoadingGlobal(true);
      await loadGlobalData();
      setLoadingGlobal(false);
      // Return to the original deep link the user came from (falls back to "/").
      navigate(consumeRedirectTarget(), { replace: true });
    };
    window.addEventListener("app:login", onLogin);
    return () => window.removeEventListener("app:login", onLogin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ThemeProvider />
      {/* One loading indicator for the whole app: every <Spin> — with or without
          a `size` — renders the logo spinner instead of antd's dots. */}
      <ConfigProvider
        theme={antdTheme}
        // AntD ships its own strings — the sorter tooltip, "No data", the date
        // picker, "items / page". Without a locale they stay English on an
        // otherwise Vietnamese screen.
        locale={isEnglish ? enUS : viVN}
        form={{ requiredMark: false }}
        spin={{ indicator: <LogoSpinner /> }}
      >
        {/* <CustomLoading /> */}
        {loadingGlobal && <CustomLoading />}

        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          theme="dark"
          pauseOnHover={true}
        />
        {!loadingGlobal &&
          (isOauth2WithoutToken() && location.pathname !== "/login" ? (
            // Not logged in (OAUTH2): redirect to /login synchronously so no
            // protected content flashes before checkAuth runs — and no loader.
            <Navigate to="/login" replace />
          ) : (
            <RouterPage />
          ))}

        {/* Global Download Progress Panel */}
        <DownloadProgressPanel
          files={downloadingFiles}
          onCancel={() => dispatch(clearAllDownloads())}
          onRetry={handleRetry}
          onCancelItem={handleCancelItem}
        />
      </ConfigProvider>
    </>
  );
}
